// みんなのピース 専用の左パネル（検索 / こうかい / そのほか / ならびかえ）。
// MINE とは独立した専用コンポーネント（今後 PUBLIC 独自に変更可）。
// 低レベル部品（SidebarDropdown / MenuOptionRow / MenuActionFooter）のみ共有。
import { useState } from "react";
import I18n from "common/i18n";
import {
  PublicFilterState,
  PublicOfficialFilter,
  PieceSort,
  PieceSortOrder,
} from "features/builder/pieceList";
import { MenuOptionRow } from "./MenuOptionRow";
import { SidebarSection } from "./SidebarSection";
import { PublicSortMenu } from "./PublicSortMenu";
import { SidebarDropdown } from "./SidebarDropdown";
import { MenuActionFooter } from "./MenuActionFooter";
import { useBuilderSidebar, SidebarToggleButton, SidebarHeaderBand } from "./BuilderSidebar";

const ACCENT_TEXT = "text-blue-100";
const ACCENT_BG = "bg-blue-100";
const ACCENT_BORDER = "border-blue-100";

const OFFICIAL_ITEMS: { value: PublicOfficialFilter; labelKey: string; icon: string }[] = [
  { value: PublicOfficialFilter.ALL, labelKey: "MSG_BUILDER_FILTER_ALL", icon: "category" },
  {
    value: PublicOfficialFilter.OFFICIAL,
    labelKey: "MSG_BUILDER_PUBLIC_FILTER_OFFICIAL",
    icon: "verified",
  },
  {
    value: PublicOfficialFilter.UNOFFICIAL,
    labelKey: "MSG_BUILDER_PUBLIC_FILTER_UNOFFICIAL",
    icon: "person",
  },
];

// そのほかトグル項目（お気に入り/コピー可/リミックス/にんき/コメント可）
type OtherDraft = {
  favorite: boolean;
  copyable: boolean;
  remix: boolean;
  popular: boolean;
  commentable: boolean;
};

const OTHER_ITEMS: { key: keyof OtherDraft; labelKey: string; icon: string }[] = [
  { key: "favorite", labelKey: "MSG_BUILDER_FILTER_FAVORITE", icon: "favorite" },
  { key: "copyable", labelKey: "MSG_BUILDER_PUBLIC_FILTER_COPYABLE", icon: "content_copy" },
  { key: "remix", labelKey: "MSG_BUILDER_PUBLIC_FILTER_REMIX", icon: "cached" },
  { key: "popular", labelKey: "MSG_BUILDER_PUBLIC_FILTER_POPULAR", icon: "local_fire_department" },
  { key: "commentable", labelKey: "MSG_BUILDER_PUBLIC_FILTER_COMMENTABLE", icon: "chat_bubble" },
];

const SORT_OPTIONS: PieceSort[] = [
  PieceSort.LIKED,
  PieceSort.PLAYED,
  PieceSort.VIEWED,
  PieceSort.REACTION,
];

const EMPTY_OTHER: OtherDraft = {
  favorite: false,
  copyable: false,
  remix: false,
  popular: false,
  commentable: false,
};

const officialLabel = (t: PublicOfficialFilter): string =>
  t === PublicOfficialFilter.OFFICIAL
    ? "MSG_BUILDER_PUBLIC_FILTER_OFFICIAL"
    : t === PublicOfficialFilter.UNOFFICIAL
      ? "MSG_BUILDER_PUBLIC_FILTER_UNOFFICIAL"
      : "MSG_BUILDER_PUBLIC_FILTER_TEMPLATE_AXIS";

const officialIcon = (t: PublicOfficialFilter): string =>
  t === PublicOfficialFilter.OFFICIAL
    ? "verified"
    : t === PublicOfficialFilter.UNOFFICIAL
      ? "person"
      : "category";

const otherCount = (f: PublicFilterState): number =>
  (f.favorite ? 1 : 0) +
  (f.copyable ? 1 : 0) +
  (f.remix ? 1 : 0) +
  (f.popular ? 1 : 0) +
  (f.commentable ? 1 : 0);

// しゅるい吹き出しの中身（全部/公式/非公式の排他選択。下書き＋確定/キャンセル）
const PublicOfficialPanel = ({
  official,
  onApply,
  onClose,
}: {
  official: PublicOfficialFilter;
  onApply: (next: PublicOfficialFilter) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState(official);
  return (
    <div className="flex-col-el items-start w-full gap-3">
      <div className="flex-col-el w-full gap-1">
        {OFFICIAL_ITEMS.map((item) => (
          <MenuOptionRow
            key={item.value}
            icon={item.icon}
            labelKey={item.labelKey}
            active={draft === item.value}
            activeBgClass={ACCENT_BG}
            onClick={() => setDraft(item.value)}
          />
        ))}
      </div>
      <MenuActionFooter
        canApply={draft !== official}
        accentBgClass={ACCENT_BG}
        onApply={() => {
          onApply(draft);
          onClose();
        }}
        secondaryLabel="MSG_STOP_BTN"
        onSecondary={onClose}
      />
    </div>
  );
};

// そのほか吹き出しの中身（複数トグル。下書き＋確定/クリア）
const PublicOtherPanel = ({
  value,
  onApply,
  onClose,
}: {
  value: OtherDraft;
  onApply: (next: OtherDraft) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState<OtherDraft>(value);
  const canApply = OTHER_ITEMS.some((item) => draft[item.key] !== value[item.key]);
  return (
    <div className="flex-col-el items-start w-full gap-3">
      <div className="grid grid-cols-2 gap-2 w-full">
        {OTHER_ITEMS.map((item) => (
          <MenuOptionRow
            key={item.key}
            icon={item.icon}
            labelKey={item.labelKey}
            active={draft[item.key]}
            activeBgClass={ACCENT_BG}
            onClick={() => setDraft((s) => ({ ...s, [item.key]: !s[item.key] }))}
          />
        ))}
      </div>
      <MenuActionFooter
        canApply={canApply}
        accentBgClass={ACCENT_BG}
        onApply={() => {
          onApply(draft);
          onClose();
        }}
        secondaryLabel="MSG_BUILDER_CLEAR_BTN"
        onSecondary={() => setDraft(EMPTY_OTHER)}
      />
    </div>
  );
};

export const PublicMapsHeader = ({
  count,
  filter,
  sortAxis,
  sortOrder,
  onChangeFilter,
  onChangeSort,
}: {
  count: number;
  filter: PublicFilterState;
  sortAxis: PieceSort;
  sortOrder: PieceSortOrder;
  onChangeFilter: (next: PublicFilterState) => void;
  onChangeSort: (axis: PieceSort, order: PieceSortOrder) => void;
}) => {
  const { collapsed, toggle } = useBuilderSidebar();
  return (
    <>
      {collapsed ? (
        <div
          onClick={toggle}
          className="flex-col-el items-center w-full gap-1 py-2 border-0 border-b-2 border-solid border-beige-60 cursor-pointer"
        >
          <SidebarToggleButton />
          <span className="flex-col-center w-10 h-10">
            <span className={`material-symbols-outlined type-md ${ACCENT_TEXT}`}>public</span>
          </span>
        </div>
      ) : (
        <SidebarHeaderBand>
          <span className={`material-symbols-outlined type-md ${ACCENT_TEXT}`}>public</span>
          <p className="type-text type-body text-textcolor-black whitespace-nowrap">
            {I18n.t("MSG_BUILDER_PUBLIC_COUNT", { count })}
          </p>
        </SidebarHeaderBand>
      )}

      {/* しぼりこみ */}
      <SidebarSection
        icon="filter_alt"
        labelKey="MSG_BUILDER_SECTION_FILTER"
        accentClass={ACCENT_TEXT}
      >
        <div className="flex-col-el items-start w-full gap-2">
          <SidebarDropdown
            icon={officialIcon(filter.official)}
            label={officialLabel(filter.official)}
            modalTitle="MSG_BUILDER_PUBLIC_FILTER_TEMPLATE_AXIS"
            modalIcon="category"
            accentTextClass={ACCENT_TEXT}
            accentBgClass={ACCENT_BG}
            accentBorderClass={ACCENT_BORDER}
            active={filter.official !== PublicOfficialFilter.ALL}
            wide
          >
            {(close) => (
              <PublicOfficialPanel
                official={filter.official}
                onApply={(t) => onChangeFilter({ ...filter, official: t })}
                onClose={close}
              />
            )}
          </SidebarDropdown>

          <SidebarDropdown
            icon="tune"
            label="MSG_BUILDER_FILTER_AXIS_OTHER"
            modalTitle="MSG_BUILDER_FILTER_AXIS_OTHER"
            modalIcon="tune"
            showClose
            accentTextClass={ACCENT_TEXT}
            accentBgClass={ACCENT_BG}
            accentBorderClass={ACCENT_BORDER}
            active={otherCount(filter) > 0}
            badge={otherCount(filter)}
            wide
          >
            {(close) => (
              <PublicOtherPanel
                value={{
                  favorite: filter.favorite,
                  copyable: filter.copyable,
                  remix: filter.remix,
                  popular: filter.popular,
                  commentable: filter.commentable,
                }}
                onApply={(v) => onChangeFilter({ ...filter, ...v })}
                onClose={close}
              />
            )}
          </SidebarDropdown>
        </div>
      </SidebarSection>

      {/* ならびかえ */}
      <SidebarSection
        icon="swap_vert"
        labelKey="MSG_BUILDER_SECTION_SORT"
        accentClass={ACCENT_TEXT}
      >
        <PublicSortMenu
          axis={sortAxis}
          order={sortOrder}
          options={SORT_OPTIONS}
          accentTextClass={ACCENT_TEXT}
          accentBgClass={ACCENT_BG}
          accentBorderClass={ACCENT_BORDER}
          onChange={onChangeSort}
        />
      </SidebarSection>
    </>
  );
};
